import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Production } from 'src/app/shared/models/production.model';

import { getSelectedProduction } from 'src/app/shared/store/selectors/production.selectors';

import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';
import { SiteState } from '../../../shared/models';
import { ProductionSummary, ProductionSummaryReport } from '../../models/production.model';
import * as AttentionLimitsActions from '../../store/actions/attention-limits.actions';
import * as ProductionActions from '../../store/actions/production.actions';
import { getProductionSummary } from '../../store/selectors/production.selectors';
import { AttentionSummaryComponent } from '../attention-summary/attention-summary.component';

@Component({
  selector: 'app-production',
  templateUrl: './production.component.html',
  styleUrls: ['./production.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    NotificationEmptyComponent,
    AttentionSummaryComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class ProductionComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  public selectedDate: Date;
  public selectedDate$ = new Subject<Date>();
  public selectedProduction: Production;
  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public dataSource = new MatTableDataSource<ProductionSummaryReport>();
  public footer: ProductionSummaryReport;
  public superHeader: string[] = ['before', 'dayproduction', '305-days-production', 'prediction'];

  public superHeaderSettings: { id: string; label: string; colSpan: number; flag: boolean }[] = [
    { id: 'before', label: '', colSpan: 1, flag: false },
    {
      id: 'dayproduction',
      label: 'dairyresults.production.productsummary.dayproduction',
      colSpan: 8,
      flag: true,
    },
    {
      id: '305-days-production',
      label: 'dairyresults.production.productsummary.305dayproduction',
      colSpan: 9,
      flag: true,
    },
    {
      id: 'prediction',
      label: 'dairyresults.production.productsummary.prediction',
      colSpan: 2,
      flag: true,
    },
  ];

  public columnSettings: {
    id: string;
    label: string;
    subLabel?: string;
    format?: string;
    flag?: boolean;
  }[] = [
    { id: 'groupName', label: 'dairyresults.production.productsummary.groupName' },
    {
      id: 'dayNumberOfAnimals',
      label: 'dairyresults.production.productsummary.dayNumberOfAnimals',
      flag: true,
    },
    { id: 'daylactationDays', label: 'dairyresults.production.productsummary.daylactationDays' },
    {
      id: 'dayKgMilk',
      label: 'dairyresults.production.productsummary.dayKgMilk',
      subLabel: 'dairyresults.production.productsummary.dayKgMilk-unit',
      format: '1.1-1',
    },
    {
      id: 'dayFatPercentage',
      label: 'dairyresults.production.productsummary.dayFatPercentage',
      subLabel: '%',
      format: '1.2-2',
    },
    {
      id: 'dayProteinPercentage',
      label: 'dairyresults.production.productsummary.dayProteinPercentage',
      subLabel: '%',
      format: '1.2-2',
    },
    {
      id: 'dayLactosePercentage',
      label: 'dairyresults.production.productsummary.dayLactosePercentage',
      subLabel: '%',
      format: '1.2-2',
    },
    { id: 'dayUreum', label: 'dairyresults.production.productsummary.dayUreum', format: '1.0-0' },
    {
      id: 'dayKgFatProtein',
      label: 'dairyresults.production.productsummary.dayKgFatProtein',
      subLabel: 'dairyresults.production.productsummary.dayKgFatProtein-unit',
      format: '1.2-2',
    },
    {
      id: 'days305Age',
      label: 'dairyresults.production.productsummary.days305Age',
      subLabel: 'dairyresults.production.productsummary.days305Age-unit',
      format: '1.2-2',
      flag: true,
    },
    {
      id: 'days305NumberOfAnimals',
      label: 'dairyresults.production.productsummary.days305NumberOfAnimals',
    },
    {
      id: 'days305KgMilk',
      label: 'dairyresults.production.productsummary.days305KgMilk',
      format: '1.0-0',
    },
    {
      id: 'days305FatPercentage',
      label: 'dairyresults.production.productsummary.days305FatPercentage',
      subLabel: '%',
      format: '1.2-2',
    },
    {
      id: 'days305ProteinPercentage',
      label: 'dairyresults.production.productsummary.days305ProteinPercentage',
      subLabel: '%',
      format: '1.2-2',
    },
    {
      id: 'days305KgFat',
      label: 'dairyresults.production.productsummary.days305KgFat',
      subLabel: '%',
      format: '1.0-0',
    },
    {
      id: 'days305KgProtein',
      label: 'dairyresults.production.productsummary.days305KgProtein',
      subLabel: '%',
      format: '1.0-0',
    },
    {
      id: 'days305NetProduction',
      label: 'dairyresults.production.productsummary.days305NetProduction',
      subLabel: 'dairyresults.production.productsummary.days305NetProduction-unit',
      format: '1.0-0',
    },
    {
      id: 'days305lactation',
      label: 'dairyresults.production.productsummary.days305lactation',
      format: '1.0-0',
    },
    {
      id: 'bskNumberOfAnimals',
      label: 'dairyresults.production.productsummary.bskNumberOfAnimals',
      flag: true,
    },
    { id: 'bskValue', label: 'dairyresults.production.productsummary.bskValue', format: '1.1-1' },
  ];

  private productionSummary$ = this.store.select(getProductionSummary);
  private productionSelected$ = this.store.select(getSelectedProduction);
  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'dairyresults.production.title' }),
    );
    this.productionSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((productionSummary) => this.onProductionSummaryLoaded(productionSummary));
    this.selectedDate$.subscribe((date) => {
      this.selectedDate = date;
    });
    this.productionSelected$
      .pipe(
        filter((selectedProduction) => !!selectedProduction),
        takeUntil(this.destroy$),
      )
      .subscribe((selectedProduction) => {
        this.selectedProduction = selectedProduction;
        this.selectedDate = new Date(selectedProduction.date);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public superHeaders = (): string[] => this.superHeaderSettings.map((item) => item.id);
  public columnIds = (): string[] => this.columnSettings.map((item) => item.id);

  public onProductionChanged(production: Production) {
    this.store.dispatch(
      ProductionActions.loadProductionSummaryRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
    this.store.dispatch(
      AttentionLimitsActions.loadAttentionLimitsRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
  }

  public hasData = (): boolean => this.dataSource.data.length > 0;

  private onProductionSummaryLoaded(productionSummary: ProductionSummary) {
    if (productionSummary === null) {
      return;
    }
    const tableData: ProductionSummaryReport[] = productionSummary?.groups.map((g) => ({
      groupName: this.localeService.translate(
        `dairyresults.production.productsummary.group-${g.name}`,
      ),
      dayNumberOfAnimals: g.dayProduction.cows?.length,
      daylactationDays: g.dayProduction.lactationDays,
      dayKgMilk: g.dayProduction.kgMilk,
      dayFatPercentage: g.dayProduction.fatPercentage,
      dayProteinPercentage: g.dayProduction.proteinPercentage,
      dayLactosePercentage: g.dayProduction.lactosePercentage,
      dayKgFatProtein: g.dayProduction.kgFatProtein,
      dayUreum: g.dayProduction.ureum,
      days305Age: g.age,
      days305NumberOfAnimals: g.production305Days.cows?.length,
      days305KgMilk: g.production305Days.kgMilk,
      days305FatPercentage: g.production305Days.fatPercentage,
      days305ProteinPercentage: g.production305Days.proteinPercentage,
      days305KgFat: g.production305Days.kgFat,
      days305KgProtein: g.production305Days.kgProtein,
      days305NetProduction: g.production305Days.netProduction,
      days305lactation: g.production305Days.lactation,
      bskNumberOfAnimals: g.bsk?.numberOfAnimals,
      bskValue: g.bsk?.value,
    }));

    this.footer = tableData.pop();
    tableData.splice(3, 0, {});
    this.dataSource.data = tableData;
  }
}
